import {
  ACTIVE_DESIGN_SYSTEM_VERSION,
  getDesignSystem,
} from '../../../../../../layouts/theme/designSystem';
import type {DesignSystemVersion} from '../../../../../../layouts/theme/designSystem';

export type MatchesBoardKanbanColumnKey =
  | 'new'
  | 'contacted'
  | 'interview'
  | 'inProgress'
  | 'hired'
  | 'rejected'
  | 'onHold';

export type MatchesBoardColumnToken = {
  background: string;
  accent: string;
};

export type MatchesBoardTokens = {
  columns: Record<MatchesBoardKanbanColumnKey, MatchesBoardColumnToken>;
  acceptedCardShadow: string;
  acceptedCardShadowHover: string;
  dropTargetOutline: string;
  columnHeaderHoverBg: string;
  countChipBorder: string;
  layoutToggleActive: string;
  layoutToggleTrack: string;
  surfaceBackground: string;
  surfaceShadow: string;
  cardShadow: string;
  cardShadowHover: string;
};

const buildMatchesBoard20260615 = (version: DesignSystemVersion): MatchesBoardTokens => {
  const {greenColors, orangeColors} = getDesignSystem(version);

  return {
    columns: {
      new: {background: '#f3f5f4', accent: greenColors[40]},
      contacted: {background: 'rgba(224, 107, 43, 0.1)', accent: orangeColors[6]},
      interview: {background: greenColors[50], accent: greenColors[100]},
      inProgress: {background: '#f7f4ee', accent: orangeColors[40]},
      hired: {background: '#f2f0ed', accent: greenColors[50]},
      rejected: {background: '#f7f0eb', accent: orangeColors[7]},
      onHold: {background: '#efeeeb', accent: orangeColors[5]},
    },
    acceptedCardShadow:
      '0 2px 8px rgba(9, 44, 76, 0.07), 0 3px 14px rgba(224, 139, 88, 0.16), 0 1px 3px rgba(9, 44, 76, 0.05)',
    acceptedCardShadowHover:
      '0 4px 14px rgba(9, 44, 76, 0.09), 0 5px 20px rgba(224, 139, 88, 0.2), 0 2px 6px rgba(9, 44, 76, 0.06)',
    dropTargetOutline: orangeColors[70],
    columnHeaderHoverBg: 'rgba(9, 44, 76, 0.04)',
    countChipBorder: '1px solid rgba(9, 44, 76, 0.08)',
    layoutToggleActive: orangeColors[900],
    layoutToggleTrack: orangeColors[70],
    surfaceBackground: '#ffffff',
    surfaceShadow: '0 1px 4px rgba(9, 44, 76, 0.06), 0 2px 12px rgba(9, 44, 76, 0.04)',
    cardShadow: '0 1px 3px rgba(9, 44, 76, 0.06), 0 2px 8px rgba(9, 44, 76, 0.07)',
    cardShadowHover: '0 4px 14px rgba(9, 44, 76, 0.1), 0 2px 6px rgba(9, 44, 76, 0.06)',
  };
};

const buildMatchesBoard20260616 = (): MatchesBoardTokens => {
  const {greenColors, orangeColors, neutral} = getDesignSystem('20260616');

  return {
    columns: {
      new: {background: greenColors[5], accent: greenColors[40]},
      contacted: {background: 'rgba(212, 132, 74, 0.1)', accent: orangeColors[6]},
      interview: {background: greenColors[50], accent: greenColors[100]},
      inProgress: {background: orangeColors[5], accent: orangeColors[40]},
      hired: {background: neutral['black-50'], accent: greenColors[50]},
      rejected: {background: orangeColors[5], accent: orangeColors[7]},
      onHold: {background: greenColors[3], accent: orangeColors[5]},
    },
    acceptedCardShadow:
      '0 2px 8px rgba(30, 42, 49, 0.06), 0 3px 14px rgba(200, 106, 56, 0.12), 0 1px 3px rgba(30, 42, 49, 0.04)',
    acceptedCardShadowHover:
      '0 4px 14px rgba(30, 42, 49, 0.08), 0 5px 20px rgba(200, 106, 56, 0.16), 0 2px 6px rgba(30, 42, 49, 0.05)',
    dropTargetOutline: orangeColors[70],
    columnHeaderHoverBg: 'rgba(30, 42, 49, 0.04)',
    countChipBorder: `1px solid ${neutral[200]}`,
    layoutToggleActive: orangeColors[900],
    layoutToggleTrack: orangeColors[70],
    surfaceBackground: '#ffffff',
    surfaceShadow: '0 1px 4px rgba(30, 42, 49, 0.06), 0 2px 12px rgba(30, 42, 49, 0.04)',
    cardShadow: '0 1px 3px rgba(30, 42, 49, 0.06), 0 2px 8px rgba(30, 42, 49, 0.07)',
    cardShadowHover: '0 4px 14px rgba(30, 42, 49, 0.1), 0 2px 6px rgba(30, 42, 49, 0.06)',
  };
};

const buildMatchesBoardMonochrome = (): MatchesBoardTokens => {
  const {greenColors, orangeColors} = getDesignSystem('monochrome');

  return {
    columns: {
      new: {background: '#f2f2f2', accent: greenColors[40]},
      contacted: {background: '#e6e6e6', accent: orangeColors[70]},
      interview: {background: greenColors[50], accent: greenColors[100]},
      inProgress: {background: '#e8e8e8', accent: orangeColors[40]},
      hired: {background: '#eeeeee', accent: greenColors[50]},
      rejected: {background: '#e4e4e4', accent: orangeColors[7]},
      onHold: {background: '#eaeaea', accent: orangeColors[5]},
    },
    acceptedCardShadow:
      '0 2px 8px rgba(0, 0, 0, 0.06), 0 3px 14px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.04)',
    acceptedCardShadowHover:
      '0 4px 14px rgba(0, 0, 0, 0.08), 0 5px 20px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.05)',
    dropTargetOutline: orangeColors[70],
    columnHeaderHoverBg: 'rgba(0, 0, 0, 0.04)',
    countChipBorder: '1px solid rgba(0, 0, 0, 0.08)',
    layoutToggleActive: greenColors[500],
    layoutToggleTrack: orangeColors[70],
    surfaceBackground: '#ffffff',
    surfaceShadow: '0 1px 4px rgba(0, 0, 0, 0.06), 0 2px 12px rgba(0, 0, 0, 0.04)',
    cardShadow: '0 1px 3px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.07)',
    cardShadowHover: '0 4px 14px rgba(0, 0, 0, 0.1), 0 2px 6px rgba(0, 0, 0, 0.06)',
  };
};

export const getMatchesBoardTokens = (
  version: DesignSystemVersion = ACTIVE_DESIGN_SYSTEM_VERSION,
): MatchesBoardTokens => {
  if (version === '20260616') {
    return buildMatchesBoard20260616();
  }

  if (version === 'monochrome') {
    return buildMatchesBoardMonochrome();
  }

  return buildMatchesBoard20260615(version);
};

export const matchesBoard = getMatchesBoardTokens();
